import { Button, Modal, Form, Input, Select, Checkbox } from "antd";
import React, { useEffect } from "react";
import { useUpdateFile } from "~/services/file";
import { DocumentStatus, DocumentType } from "~/utils/constants";
import { TFile } from "~/types";
import { useNotification } from "~/hooks";

const { Option } = Select;

interface DocumentEditFormValues {
  title: string;
  type: DocumentType;
  status: DocumentStatus;
  visibleToStaff: boolean;
  visibleToClient: boolean;
}

interface DocumentEditModalProps {
  visible: boolean;
  document: TFile | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const DocumentEditModal: React.FC<DocumentEditModalProps> = ({
  visible,
  document,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const { success, error } = useNotification();
  const updateMutation = useUpdateFile();

  useEffect(() => {
    if (visible && document) {
      form.setFieldsValue({
        title: document.title,
        type: document.type,
        status: document.status,
        visibleToStaff: document.visibleToStaff ?? false,
        visibleToClient: document.visibleToClient ?? false,
      });
    }
  }, [visible, document, form]);

  const handleSubmit = async (values: DocumentEditFormValues) => {
    if (!document) return;

    try {
      await updateMutation.mutateAsync({
        id: document.id,
        title: values.title,
        type: values.type,
        status: values.status,
        visibleToStaff: values.visibleToStaff,
        visibleToClient: values.visibleToClient,
      });
      success({ message: "Document updated successfully" });
      form.resetFields();
      onSuccess();
    } catch (err) {
      console.error("Error updating document:", err);
      error({ message: "Failed to update document" });
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title="Edit Document"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={500}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        <Form.Item
          name="title"
          label="Document Title"
          rules={[{ required: true, message: "Please enter document title" }]}
        >
          <Input placeholder="Enter document title" />
        </Form.Item>

        <Form.Item
          name="type"
          label="Document Type"
          rules={[{ required: true }]}
        >
          <Select>
            <Option value={DocumentType.INVENTORY}>Inventory</Option>
            <Option value={DocumentType.ADMINISTRATIVE}>Administrative</Option>
            <Option value={DocumentType.LEGAL}>Legal</Option>
            <Option value={DocumentType.SUPPLEMENTARY}>Supplementary</Option>
            <Option value={DocumentType.OTHER}>Other</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
        >
          <Select>
            <Option value={DocumentStatus.DRAFT}>Draft</Option>
            <Option value={DocumentStatus.SENT}>Sent</Option>
            <Option value={DocumentStatus.SIGNED}>Signed</Option>
            <Option value={DocumentStatus.APPROVED}>Approved</Option>
            <Option value={DocumentStatus.REJECTED}>Rejected</Option>
          </Select>
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="visibleToStaff"
            valuePropName="checked"
            extra="Check this box to make the document visible to staff members"
          >
            <Checkbox>Visible to Staff</Checkbox>
          </Form.Item>

          <Form.Item
            name="visibleToClient"
            valuePropName="checked"
            extra="Check this box to make the document visible to clients"
          >
            <Checkbox>Visible to Client</Checkbox>
          </Form.Item>
        </div>

        <Form.Item>
          <div className="flex justify-end space-x-2">
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateMutation.isPending}
            >
              Save Changes
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};
